export interface LegalSection {
  heading: string;
  paragraphs: string[];
  bullets?: string[];
}

export interface LegalDocument {
  title: string;
  effectiveDate: string;
  intro?: string;
  sections: LegalSection[];
}

export const LEGAL_ENTITY = 'Apex Roofer Technologies, Inc.';
export const LEGAL_CONTACT_EMAIL = 'support@letsgoapex.com';
export const LEGAL_GOVERNING_LAW = 'State of Texas';
export const LEGAL_EFFECTIVE_DATE = 'July 22, 2026';

export const TERMS_OF_USE: LegalDocument = {
  title: `${LEGAL_ENTITY} — Terms of Use`,
  effectiveDate: LEGAL_EFFECTIVE_DATE,
  sections: [
    {
      heading: '1. Acceptance of Terms',
      paragraphs: [
        `By accessing or using ApexOS (the "Service"), including our website, mobile applications, and APIs, you agree to be bound by these Terms of Use ("Terms"). If you are using the Service on behalf of an organization, you represent that you have authority to bind that organization, and "you" refers to that organization. If you do not agree to these Terms, do not use the Service.`,
      ],
    },
    {
      heading: '2. The Service',
      paragraphs: [
        `ApexOS is a business management platform for roofing contractors and related trades, providing CRM, job tracking, billing, marketing, and related tools. We may modify, suspend, or discontinue any part of the Service at any time.`,
      ],
    },
    {
      heading: '3. Accounts and Eligibility',
      paragraphs: [
        `You must be at least 18 years old and able to form a binding contract. You are responsible for maintaining the confidentiality of your account credentials and for all activity under your account. You agree to provide accurate information and to keep it current. Notify us immediately at ${LEGAL_CONTACT_EMAIL} of any unauthorized use.`,
      ],
    },
    {
      heading: '4. Subscriptions, Trials, and Payment',
      paragraphs: [
        `Paid plans are billed in advance on a recurring basis (monthly or annual) and are non-refundable except where required by law. Free trials automatically convert to paid subscriptions unless cancelled before the trial ends. Fees, taxes, and plan features are described at signup and may be updated with notice. You authorize us and our payment processors to charge your designated payment method.`,
      ],
    },
    {
      heading: '5. SMS/Text Message Notifications',
      paragraphs: [
        `ApexOS offers optional SMS/text message notifications for your account ("ApexOS Notifications"), including account verification codes (one-time passcodes), security and system alerts, and operational updates such as job, task, lead, and user-role notifications. You opt in by providing your mobile phone number and checking the SMS consent checkbox at signup, or by enabling SMS notifications in your account settings. Consent is optional and is not a condition of purchasing or using the Service.`,
        `Message frequency varies based on your account activity. Message and data rates may apply. Reply STOP at any time to unsubscribe (reply START to resubscribe) or HELP for assistance; you may also contact ${LEGAL_CONTACT_EMAIL}. Mobile carriers are not liable for delayed or undelivered messages. See our Privacy Policy for how we handle mobile phone numbers and SMS opt-in data.`,
        `These account notifications are separate from SMS you may send to your own contacts through the Service; you are solely responsible for those messages as described in Acceptable Use.`,
      ],
    },
    {
      heading: '6. Acceptable Use',
      paragraphs: ['You agree not to:'],
      bullets: [
        'Use the Service to violate any law or third-party right.',
        'Upload malware, spam, or content you do not have rights to.',
        'Attempt to gain unauthorized access to the Service, other accounts, or our infrastructure.',
        'Reverse engineer, scrape, or resell the Service except as expressly permitted.',
        'Use the Service to send unsolicited communications in violation of CAN-SPAM, TCPA, or similar laws. You are solely responsible for messages you send through the Service to your own contacts.',
      ],
    },
    {
      heading: '7. Customer Data and Content',
      paragraphs: [
        `You retain all rights to the data and content you upload ("Customer Data"). You grant us a worldwide, non-exclusive license to host, process, transmit, and display Customer Data solely to provide and improve the Service. You represent that you have all necessary rights and consents to upload Customer Data, including consent from any individuals whose information you upload (such as your customers, leads, or employees).`,
      ],
    },
    {
      heading: '8. Third-Party Services',
      paragraphs: [
        `The Service integrates with third-party providers (e.g., Mailgun, Twilio, Stripe, Plaid, Google Maps, AWS, Sentry). Your use of those services is subject to their terms and privacy policies. We are not responsible for third-party services.`,
      ],
    },
    {
      heading: '9. Intellectual Property',
      paragraphs: [
        `ApexOS, including its software, design, logos, and content (excluding Customer Data), is owned by ${LEGAL_ENTITY} and its licensors and is protected by intellectual property laws. We grant you a limited, non-exclusive, non-transferable license to use the Service in accordance with these Terms.`,
      ],
    },
    {
      heading: '10. Confidentiality',
      paragraphs: [
        `Each party agrees to protect the other's confidential information with at least the same care it uses for its own, and to use it only to perform under these Terms.`,
      ],
    },
    {
      heading: '11. Termination',
      paragraphs: [
        `You may cancel your subscription at any time from your account settings. We may suspend or terminate your access for breach of these Terms, non-payment, or as required by law. Upon termination, your right to use the Service ends; we will make Customer Data available for export for 30 days, after which we may delete it.`,
      ],
    },
    {
      heading: '12. Disclaimers',
      paragraphs: [
        `THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.`,
      ],
    },
    {
      heading: '13. Limitation of Liability',
      paragraphs: [
        `TO THE MAXIMUM EXTENT PERMITTED BY LAW, ${LEGAL_ENTITY.toUpperCase()} WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR LOST PROFITS, REVENUE, OR DATA. OUR TOTAL LIABILITY ARISING OUT OF OR RELATED TO THESE TERMS WILL NOT EXCEED THE FEES YOU PAID US IN THE 12 MONTHS PRECEDING THE CLAIM.`,
      ],
    },
    {
      heading: '14. Indemnification',
      paragraphs: [
        `You agree to indemnify and hold ${LEGAL_ENTITY} harmless from claims arising out of your Customer Data, your violation of these Terms, or your violation of any law or third-party right.`,
      ],
    },
    {
      heading: '15. Governing Law and Disputes',
      paragraphs: [
        `These Terms are governed by the laws of the ${LEGAL_GOVERNING_LAW}, without regard to conflict-of-laws principles. Any dispute arising under these Terms will be resolved exclusively in the state or federal courts located in Texas, and you consent to personal jurisdiction there.`,
      ],
    },
    {
      heading: '16. Changes to These Terms',
      paragraphs: [
        `We may update these Terms from time to time. Material changes will be communicated by email or in-app notice at least 14 days before taking effect. Continued use after the effective date constitutes acceptance.`,
      ],
    },
    {
      heading: '17. Contact',
      paragraphs: [`Questions about these Terms: ${LEGAL_CONTACT_EMAIL}`],
    },
  ],
};

export const PRIVACY_POLICY: LegalDocument = {
  title: `${LEGAL_ENTITY} — Privacy Policy`,
  effectiveDate: 'October 29, 2026',
  sections: [
    {
      heading: 'ABC Supply integration',
      paragraphs: [
        'Connecting ABC Supply is optional and requires an authorized administrator’s explicit acceptance. ApexOS receives account and branch details, product information, customer pricing and order updates. When you submit an order, ApexOS transmits order lines, delivery information and the contacts you provide to ABC Supply. You must have permission to share those details.',
        'Authorized organization members can access saved business records. OAuth credentials are encrypted on the server and are not provided to the browser. Live product and price responses are fetched on demand without an application content cache. Disconnect removes saved account details and credentials; saved order records remain subject to applicable retention requirements and ABC’s approved policy. Administrators can export distributor account and order records from the connection settings. Contact support for deletion requests or retention questions.',
        'ABC’s API Terms of Use and privacy policies govern its processing of submitted information. ApexOS does not include ABC API prices in its cross-distributor best-price recommendation. Production use remains subject to ABC’s approval, including approved data retention, processors and access permissions.',
      ],
    },
    {
      heading: '1. Introduction',
      paragraphs: [
        `This Privacy Policy explains how ${LEGAL_ENTITY} ("we," "us") collects, uses, and shares information in connection with ApexOS — our website, mobile applications, and APIs (collectively, the "Service").`,
      ],
    },
    {
      heading: '2. Information We Collect',
      paragraphs: [
        `Account Information. Name, email, phone number, password (hashed), company name, and role.`,
        `Customer Data. Information you upload to the Service, including contacts, leads, jobs, photos, documents, measurements, invoices, and communications.`,
        `Authentication Data. Login credentials, session tokens, and biometric verification data on mobile devices (Face ID / Touch ID — these are processed locally by your device and are not transmitted to us).`,
        `Usage and Device Information. Pages and features accessed, time spent, IP address, browser type, operating system, device identifiers, and crash/performance data.`,
        `Location Information. GPS coordinates when you enable location services for features such as weather reports and job site mapping.`,
        `Media. Photos and files you take or upload via the Service.`,
        `Payment Information. We do not store full payment card numbers. Payments are processed by Stripe and other PCI-compliant processors who collect this information directly.`,
        `Communications. Email and SMS we send on your behalf to your contacts, and replies routed through the Service.`,
        `Connected Email Accounts. If you connect a Google (Gmail) or Microsoft (Outlook) mailbox, we access message metadata and, when you open a message, its content — to power the Inbox and contact timelines. See "Google and Microsoft Account Data; Limited Use" below.`,
      ],
    },
    {
      heading: '3. How We Use Information',
      paragraphs: ['We use information to:'],
      bullets: [
        'Provide, maintain, and improve the Service.',
        'Authenticate you and secure your account.',
        'Process payments and prevent fraud.',
        'Send transactional emails (e.g., invoices, notifications) and, with your consent, marketing communications.',
        'Respond to support requests.',
        'Generate location-based features such as weather reports.',
        'Comply with legal obligations and enforce our Terms.',
      ],
    },
    {
      heading: '4. How We Share Information',
      paragraphs: ['We do not sell your personal information. We share information with:'],
      bullets: [
        'Service providers who help us operate the Service, including hosting (AWS, Heroku), email (Mailgun), SMS (Twilio), payments (Stripe, Plaid), error monitoring (Sentry), mapping (Google Maps), and weather/GIS data providers. These providers are contractually limited to using information only for our purposes.',
        'Within your organization. Information you upload is visible to other users in your organization with appropriate permissions.',
        'Legal compliance. We may disclose information when required by law, subpoena, or to protect rights, property, or safety.',
        'Business transfers. In connection with a merger, acquisition, or sale of assets, with notice to you.',
      ],
    },
    {
      heading: '5. SMS/Text Messaging and Mobile Information',
      paragraphs: [
        `If you opt in to receive SMS/text notifications from ApexOS (for example, by checking the SMS consent checkbox at signup or enabling them in account settings), we use your mobile phone number to send you the messages described in our Terms of Use — such as account verification codes, security alerts, and notifications about activity in your account. Consent is optional and is not a condition of purchasing or using the Service. Message frequency varies; message and data rates may apply. Reply STOP to any message to opt out, or HELP for assistance.`,
        `No mobile information will be shared with third parties or affiliates for marketing or promotional purposes. Text messaging originator opt-in data and consent will not be shared with any third parties, excluding aggregators and providers of the text message services (such as Twilio), solely to the extent necessary to deliver those messages.`,
      ],
    },
    {
      heading: '6. Data Security',
      paragraphs: [
        `We use industry-standard safeguards including TLS encryption in transit, encryption at rest for sensitive fields, role-based access controls, and routine security reviews. No system is perfectly secure; you are responsible for maintaining the confidentiality of your credentials.`,
      ],
    },
    {
      heading: '7. Data Retention',
      paragraphs: [
        `We retain Customer Data while your account is active. After termination, we retain Customer Data for up to 30 days to allow export, after which it is deleted from production systems. Backups are purged on rolling cycles. We may retain information longer where required by law.`,
      ],
    },
    {
      heading: '8. Your Rights and Choices',
      paragraphs: [
        `Depending on where you live (e.g., California, EU/UK, Texas), you may have rights to access, correct, delete, port, or restrict processing of your personal information, and to object to certain uses. To exercise these rights, email ${LEGAL_CONTACT_EMAIL}. You can also update most information directly in account settings.`,
        `California residents: You may request disclosure of categories of personal information collected and shared, and request deletion, subject to exceptions. We do not sell personal information.`,
        `Marketing communications: You can unsubscribe from marketing emails using the link in any message. Transactional messages (account, billing, security) cannot be opted out of while you have an account.`,
      ],
    },
    {
      heading: '9. Cookies and Tracking',
      paragraphs: [
        `We use cookies and similar technologies for authentication, preferences, and analytics. You can control cookies through your browser. The Service does not currently respond to "Do Not Track" signals.`,
      ],
    },
    {
      heading: '10. Mobile App Permissions',
      paragraphs: ['Our mobile app may request access to:'],
      bullets: [
        'Camera and Photos — for job documentation.',
        'Location — for weather reports and site features.',
        'Notifications — for alerts and updates.',
        'Biometrics — for secure local authentication.',
      ],
    },
    {
      heading: '11. Children',
      paragraphs: [
        `The Service is not directed to children under 16, and we do not knowingly collect personal information from them.`,
      ],
    },
    {
      heading: '12. International Users',
      paragraphs: [
        `The Service is operated from the United States. By using the Service, you understand your information may be processed in the U.S., where data protection laws may differ from your jurisdiction.`,
      ],
    },
    {
      heading: '13. Changes to This Policy',
      paragraphs: [
        `We may update this Policy from time to time. Material changes will be communicated by email or in-app notice. The "Effective Date" at the top reflects the latest version.`,
      ],
    },
    {
      heading: '14. Google and Microsoft Account Data; Limited Use',
      paragraphs: [
        `If you connect a Google (Gmail) or Microsoft (Outlook) email account, you authorize ApexOS — through Google and Microsoft OAuth — to access that mailbox on your behalf to power the ApexOS Inbox and contact timelines. This section describes how we access, use, store, and share that data, and applies in addition to the rest of this Policy.`,
        `What we access. We read message metadata (such as From, To, Cc, Subject, Date, and threading identifiers) to identify and organize your correspondence, and we retrieve the full content of an individual message only when you open that message in ApexOS. We send messages only when you compose and send them from within ApexOS. We do not modify or delete messages in your mailbox.`,
        `Contacts-only. We import a message only when it was sent to or received from an email address saved on one of your ApexOS contacts. Email with people who are not your contacts is not imported.`,
        `How we use it. We use this data solely to provide user-facing features you can see in ApexOS: showing your email correspondence with a contact on that contact's timeline and in your Inbox, and sending replies and new messages from your connected account. We do not use Google or Microsoft user data for advertising, and we do not use it to develop, improve, or train generalized artificial-intelligence or machine-learning models.`,
        `How we store and share it. Message metadata, and the content of messages you open, are stored in our database only to render your correspondence to authorized users within your organization. Attachments are retrieved on demand and are not stored by us. We do not sell this data, and we do not transfer it to others except to subprocessors that operate the Service on our behalf, to comply with applicable law, or in connection with a merger or acquisition with notice to you. We do not allow humans to read your email data except with your explicit consent for specific messages, as necessary for security or to comply with applicable law, or where the data has been aggregated and anonymized.`,
        `Revoking access. You can disconnect a mailbox at any time from Settings → Profile, and you can revoke ApexOS's access from your Google Account at myaccount.google.com/permissions or from your Microsoft account security settings. When you disconnect, we stop syncing and delete the stored OAuth tokens; correspondence already synced is retained as part of your organization's business records unless you request its deletion at ${LEGAL_CONTACT_EMAIL}.`,
        `Limited Use. ApexOS's use and transfer of information received from Google APIs to any other app will adhere to the Google API Services User Data Policy, including the Limited Use requirements. Our use of information received from Microsoft Graph likewise complies with the Microsoft APIs Terms of Use and applicable use limitations.`,
      ],
    },
    {
      heading: '15. Contact',
      paragraphs: [`Questions or requests regarding this Policy: ${LEGAL_CONTACT_EMAIL}`],
    },
  ],
};
